import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, LineChart, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";

const inputCls = "w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors";

const perks = [
  { icon: Sparkles, title: "Early access", body: "First look at new compounds and restocks before they go public, so your audience hears it from you first." },
  { icon: LineChart, title: "Real-time dashboard", body: "Watch clicks, conversions, and referrals update as your audience buys." },
  { icon: LifeBuoy, title: "Full asset kit", body: "Product imagery, spec sheets, and COAs ready to share with your audience." },
];

export default function Affiliate() {
  const [form, setForm] = useState({ name: "", email: "", company: "", audience: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/affiliate", form);
      setSent(true);
      toast.success("Application submitted.");
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="affiliate-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <FadeUp>
          <p className="label text-accent mb-6">/ Partners</p>
          <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.9] tracking-tight max-w-4xl">
            Become an affiliate.
          </h1>
          <p className="mt-8 text-sm text-muted-foreground max-w-xl leading-relaxed">
            Researchers, educators, and creators — share research-grade compounds your audience can trust. Terms are agreed one‑to‑one after we review your application.
          </p>
        </FadeUp>

        <Stagger className="mt-16 border-t border-l border-border grid md:grid-cols-3">
          {perks.map((p) => {
            const Icon = p.icon;
            return (
              <StaggerItem key={p.title}>
                <div className="border-r border-b border-border p-8 h-full" data-testid={`perk-${p.title.split(" ")[0].toLowerCase()}`}>
                  <Icon size={28} strokeWidth={1.25} className="text-accent" />
                  <h3 className="mt-8 font-display text-2xl font-light">{p.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>

        <div className="mt-20 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <h2 className="font-display text-4xl font-light leading-tight tracking-tight">Apply to the program</h2>
            <p className="mt-4 text-sm text-muted-foreground max-w-sm">Tell us about your audience — we review every application within a few days.</p>
          </div>
          <div className="md:col-span-8">
            {sent ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-border p-12" data-testid="affiliate-success">
                <div className="h-16 w-16 flex items-center justify-center bg-accent text-accent-foreground"><Check size={32} strokeWidth={1.5} /></div>
                <h3 className="mt-8 font-display text-4xl font-light">Application received.</h3>
                <p className="mt-4 text-sm text-muted-foreground max-w-md">Thanks, {form.name.split(" ")[0]}. We will review and reach out to {form.email} soon.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="border border-border p-8 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="affiliate-form">
                <div>
                  <label className="label text-muted-foreground block mb-2">Name</label>
                  <input name="name" required value={form.name} onChange={onChange} className={inputCls} data-testid="affiliate-name" />
                </div>
                <div>
                  <label className="label text-muted-foreground block mb-2">Email</label>
                  <input name="email" type="email" required value={form.email} onChange={onChange} className={inputCls} data-testid="affiliate-email" />
                </div>
                <div>
                  <label className="label text-muted-foreground block mb-2">Company / brand</label>
                  <input name="company" value={form.company} onChange={onChange} className={inputCls} data-testid="affiliate-company" />
                </div>
                <div>
                  <label className="label text-muted-foreground block mb-2">Audience size / channel</label>
                  <input name="audience" value={form.audience} onChange={onChange} placeholder="e.g. 25k newsletter, YouTube" className={inputCls} data-testid="affiliate-audience" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label text-muted-foreground block mb-2">Tell us more (optional)</label>
                  <textarea name="message" rows={4} value={form.message} onChange={onChange} className={`${inputCls} resize-none`} data-testid="affiliate-message" />
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={busy} className="w-full h-14 bg-primary text-primary-foreground label hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50" data-testid="affiliate-submit">
                    {busy ? "Submitting…" : "Submit application"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
